import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../api/httpClient";
import InfoCard from "../../components/card/infocard";
import {
  DeleteItem,
  InviteModal,
  SuccessfulDelete,
  SuccessModal,
} from "../../components/modal";
import DeleteImg from "../../assets/delLogo.png";
import verifyImg from "../../assets/verifyImg.png";
import { createInitialsAvatar } from "../../utils/initialsAvatar";
import { SimpleButton } from "../../components/button.jsx/Btn";
import { buildAssetUrl, getApiBaseUrl } from "../../data/authStorage";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";
import { toast } from "react-toastify";

const DepartmentViewdetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const apiBaseUrl = getApiBaseUrl();
  const departmentId = useMemo(
    () => location.state?.departmentId || sessionStorage.getItem("selectedDepartmentId"),
    [location.state],
  );
  const cacheKey = useMemo(() => `cehr_department_details_${departmentId || "unknown"}`, [departmentId]);
  const cachedData = useMemo(() => readSessionCache(cacheKey, { department: null }), [cacheKey]);

  const [department, setDepartment] = useState(cachedData?.department || null);
  const [isLoading, setIsLoading] = useState(!cachedData?.department);
  const [errorMessage, setErrorMessage] = useState("");
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openDeleteSuccessModal, setOpenDeleteSuccessModal] = useState(false);
  const [departmentName, setDepartmentName] = useState("");
  const [description, setDescription] = useState("");
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [deleteErrorMessage, setDeleteErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openStaffModal, setOpenStaffModal] = useState(false);
  const [departmentStaff, setDepartmentStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffErrorMessage, setStaffErrorMessage] = useState("");
  const [assigningHeadId, setAssigningHeadId] = useState("");
  const [openManageStaffModal, setOpenManageStaffModal] = useState(false);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [initialMemberIds, setInitialMemberIds] = useState([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersSaving, setMembersSaving] = useState(false);

  const resolveEmployeeImage = (imagePath, name) => {
    return buildAssetUrl(imagePath) || createInitialsAvatar(name);
  };

  const getDepartmentStaffEmployeeId = (staff) => {
    return staff?.employeeId || staff?.employee?.id || staff?.employee?.employeeId || staff?.id || "";
  };

  const getDepartmentStaffAssignmentCandidates = (staff) => {
    return [
      staff?.employeeId,
      staff?.employee?.id,
      staff?.employee?.employeeId,
      staff?.id,
      staff?.employeeNumber,
      staff?.employee?.employeeNumber,
      staff?.idNo,
    ]
      .map((value) => `${value || ""}`.trim())
      .filter(Boolean)
      .filter((value, index, values) => values.indexOf(value) === index);
  };

  const formatEmployeeName = (employee) => {
    if (!employee) {
      return "-";
    }

    if (employee.fullName) {
      return employee.fullName;
    }

    return `${employee.firstName || ""} ${employee.lastName || ""}`.trim() || "Unnamed staff";
  };

  useEffect(() => {
    const loadDepartment = async (silent = false) => {
      if (!apiBaseUrl || !departmentId) {
        setErrorMessage(!departmentId ? "Select a department from the list first." : "API base URL is not configured.");
        return;
      }

      try {
        if (!silent) {
          setIsLoading(true);
        }
        setErrorMessage("");

        const response = await axios.get(`${apiBaseUrl}/organizations/departments/${departmentId}`);
        const loadedDepartment = response?.data?.department || null;

        setDepartment(loadedDepartment);
        setDepartmentName(loadedDepartment?.departmentName || "");
        setDescription(loadedDepartment?.description || "");
        writeSessionCache(cacheKey, { department: loadedDepartment });
      } catch (error) {
        const responseMessage =
          error?.response?.data?.message || "Unable to load department details. Please try again.";
        setErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadDepartment(true);

    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadDepartment(true);
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [apiBaseUrl, cacheKey, departmentId]);

  const refreshDepartment = async () => {
    if (!apiBaseUrl || !departmentId) {
      return;
    }

    const response = await axios.get(`${apiBaseUrl}/organizations/departments/${departmentId}`);
    const loadedDepartment = response?.data?.department || null;

    setDepartment(loadedDepartment);
    setDepartmentName(loadedDepartment?.departmentName || "");
    setDescription(loadedDepartment?.description || "");
  };

  const loadDepartmentStaff = async () => {
    if (!departmentId || !apiBaseUrl) {
      setStaffErrorMessage("Unable to load department staff right now.");
      return;
    }

    try {
      setStaffLoading(true);
      setStaffErrorMessage("");

      const response = await axios.get(`${apiBaseUrl}/organizations/departments/${departmentId}/staff`);
      setDepartmentStaff(response?.data?.staff || []);
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to load department staff. Please try again.";
      setStaffErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
      setDepartmentStaff([]);
    } finally {
      setStaffLoading(false);
    }
  };

  const openDepartmentStaffModal = async () => {
    setOpenStaffModal(true);
    await loadDepartmentStaff();
  };

  const closeDepartmentStaffModal = () => {
    setOpenStaffModal(false);
    setStaffErrorMessage("");
  };

  const openManageEmployees = async () => {
    if (!departmentId || !apiBaseUrl) return;
    setOpenManageStaffModal(true);
    setMembersLoading(true);
    setEmployeeSearch("");
    try {
      const [employeesResponse, staffResponse] = await Promise.all([
        axios.get(`${apiBaseUrl}/organizations/employees`, { params: { status: "approved" } }),
        axios.get(`${apiBaseUrl}/organizations/departments/${departmentId}/staff`),
      ]);
      const employees = employeesResponse?.data?.employees || [];
      const memberIds = (staffResponse?.data?.staff || []).map((employee) => employee.id);
      setAvailableEmployees(employees);
      setInitialMemberIds(memberIds);
      setSelectedMemberIds(memberIds);
    } catch (error) {
      const responseMessage = error?.response?.data?.message || "Unable to load employees.";
      const message = Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage;
      toast.error(message, { toastId: `load-department-members:${message}` });
    } finally {
      setMembersLoading(false);
    }
  };

  const saveDepartmentEmployees = async () => {
    const addEmployeeIds = selectedMemberIds.filter((id) => !initialMemberIds.includes(id));
    const removeEmployeeIds = initialMemberIds.filter((id) => !selectedMemberIds.includes(id));
    if (!addEmployeeIds.length && !removeEmployeeIds.length) {
      setOpenManageStaffModal(false);
      return;
    }
    try {
      setMembersSaving(true);
      try {
        await axios.patch(`${apiBaseUrl}/organizations/departments/${departmentId}/staff`, {
          addEmployeeIds,
          removeEmployeeIds,
        }, { suppressGlobalErrorToast: true });
      } catch (bulkError) {
        const routeUnavailable = bulkError?.response?.status === 404 && `${bulkError?.response?.data?.message || ""}`.includes("Cannot PATCH");
        if (!routeUnavailable || removeEmployeeIds.length) throw bulkError;

        // Compatibility path for servers not yet restarted with the new bulk
        // route. Existing primary memberships remain untouched; this selected
        // department is appended as a secondary membership.
        await Promise.all(addEmployeeIds.map(async (employeeId) => {
          const employee = availableEmployees.find((item) => item.id === employeeId);
          const detailsResponse = await axios.get(`${apiBaseUrl}/organizations/employees/${encodeURIComponent(employeeId)}`);
          const employeeDetails = detailsResponse?.data?.employee || detailsResponse?.data || {};
          const currentDepartmentIds = Array.from(new Set([
            ...((employeeDetails?.departments || []).map((item) => item?.id).filter(Boolean)),
            ...(employee?.departmentIds || []),
            ...(employee?.departmentId ? [employee.departmentId] : []),
            departmentId,
          ]));
          return axios.patch(`${apiBaseUrl}/organizations/employees/${encodeURIComponent(employeeId)}`, {
            departmentIds: currentDepartmentIds,
          });
        }));
      }
      await Promise.all([refreshDepartment(), loadDepartmentStaff()]);
      setOpenManageStaffModal(false);
      setOpenSuccessModal(true);
      toast.success("Department employees updated successfully.");
    } catch (error) {
      const responseMessage = error?.response?.data?.message || "Unable to update department employees.";
      const message = Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage;
      toast.error(message, { toastId: `department-members:${message}` });
    } finally {
      setMembersSaving(false);
    }
  };

  const handleAssignHeadOfDepartment = async (staff) => {
    const assignmentCandidates = getDepartmentStaffAssignmentCandidates(staff);

    if (!departmentId || !apiBaseUrl || assignmentCandidates.length === 0) {
      setStaffErrorMessage("Unable to assign department head right now.");
      return;
    }

    try {
      setAssigningHeadId(assignmentCandidates[0]);
      setStaffErrorMessage("");

      let lastError = null;

      for (const employeeId of assignmentCandidates) {
        try {
          await axios.patch(`${apiBaseUrl}/organizations/departments/${departmentId}/head`, {
            employeeId,
          });
          lastError = null;
          break;
        } catch (error) {
          lastError = error;

          const responseMessage = `${error?.response?.data?.message || ""}`.toLowerCase();
          const notFoundInDepartment = responseMessage.includes("not found in this department") || error?.response?.status === 404;

          if (!notFoundInDepartment) {
            throw error;
          }
        }
      }

      if (lastError) {
        throw lastError;
      }

      await Promise.all([refreshDepartment(), loadDepartmentStaff()]);
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to assign this staff member as department head.";
      setStaffErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setAssigningHeadId("");
    }
  };

  const handleOpenEditModal = () => {
    setFormErrorMessage("");
    setOpenEditModal(true);
  };

  const openDeleteDepartment = () => {
    setDeleteErrorMessage("");
    setOpenDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setOpenDeleteModal(false);
    setDeleteErrorMessage("");
  };

  const handleUpdateDepartment = async (ev) => {
    ev.preventDefault();

    if (!departmentId || !apiBaseUrl) {
      setFormErrorMessage("Unable to update this department right now.");
      return;
    }

    if (!departmentName.trim() || !description.trim()) {
      setFormErrorMessage("Department name and description are required.");
      return;
    }

    try {
      setIsSaving(true);
      setFormErrorMessage("");

      await axios.patch(`${apiBaseUrl}/organizations/departments/${departmentId}`, {
        departmentName: departmentName.trim(),
        description: description.trim(),
      });

      await refreshDepartment();
      setOpenEditModal(false);
      setOpenSuccessModal(true);
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to update department. Please try again.";
      setFormErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteDepartment = async () => {
    if (!departmentId || !apiBaseUrl) {
      setDeleteErrorMessage("Unable to delete this department right now.");
      return;
    }

    try {
      setIsDeleting(true);
      setDeleteErrorMessage("");

      await axios.delete(`${apiBaseUrl}/organizations/departments/${departmentId}`);

      setOpenDeleteModal(false);
      setOpenDeleteSuccessModal(true);
      sessionStorage.removeItem("selectedDepartmentId");

      setTimeout(() => {
        navigate("/company-settings/departments");
      }, 1200);
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to delete department. Please try again.";
      setDeleteErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const title = department?.departmentName || "Department";
  const descriptionText =
    department?.description || "No department description available yet.";
  const headOfDepartment = department?.headOfDepartment || null;
  const createdAt = department?.createdAt
    ? new Date(department.createdAt).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "-";

  return (
    <div>
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Departments", path: "/company-settings/departments" },
          { label: title },
        ]}
        title={title}
        subtitle="Department details and management actions."
        fallbackPath="/company-settings/departments"
        backLabel="Back to Departments"
        actions={(
          <div className="flex items-center gap-3">
            <button
              onClick={handleOpenEditModal}
              className="border border-[#1234CD] text-[#1234CD] px-4 py-2 rounded-xl"
            >
              Edit
            </button>
            <button
              onClick={openDeleteDepartment}
              className="border border-red-500 text-red-600 px-4 py-2 rounded-xl"
            >
              Delete
            </button>
          </div>
        )}
      />

      {isLoading && !department ? (
        <FetchLoadingState message="Loading department details..." className="min-h-[60vh]" />
      ) : null}

      {errorMessage && !department ? (
        <div className="px-6 py-4 text-sm text-red-600" role="alert">
          {errorMessage}
        </div>
      ) : null}

      {!isLoading && department ? (
        <InfoCard
          details="Department Details"
          title={title}
          description={descriptionText}
          metaData={[
            { label: "Date created", value: createdAt },
            { label: "Total staff", value: department?.totalStaff ?? departmentStaff.length ?? 0 },
          ]}
          image={resolveEmployeeImage(headOfDepartment?.profilePhotoUrl, formatEmployeeName(headOfDepartment))}
          hod={headOfDepartment ? formatEmployeeName(headOfDepartment) : "Head of Department"}
          hodName={
            headOfDepartment
              ? `${headOfDepartment?.positionName || "Staff"}${
                  headOfDepartment?.officialEmail ? ` • ${headOfDepartment.officialEmail}` : ""
                }`
              : "No head assigned yet"
          }
        >
          <div className="mt-2 flex flex-wrap justify-start gap-3 w-full">
            <button
              type="button"
              onClick={openDepartmentStaffModal}
              className="px-4 py-2 rounded-xl border border-[#1234CD] text-[#1234CD]"
            >
              View Department Staff
            </button>
            <button
              type="button"
              onClick={openManageEmployees}
              className="rounded-xl bg-[#1234CD] px-4 py-2 text-white"
            >
              Manage Employees
            </button>
            {headOfDepartment?.phone ? (
              <span className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-700">
                Phone: {headOfDepartment.phone}
              </span>
            ) : null}
          </div>
        </InfoCard>
      ) : null}

      <InviteModal
        isOpen={openStaffModal}
        onClose={closeDepartmentStaffModal}
      >
        <div className="my-4">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <p className="text-2xl font-semibold text-black font-Outfit">Department Staff</p>
              <p className="text-sm text-gray-600 mt-1">
                {title}: {departmentStaff.length} staff member{departmentStaff.length === 1 ? "" : "s"}
              </p>
            </div>
            <button
              type="button"
              onClick={loadDepartmentStaff}
              disabled={staffLoading}
              className="px-4 py-2 rounded-lg border border-[#1234CD] text-[#1234CD] disabled:opacity-60"
            >
              {staffLoading ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          {staffErrorMessage ? (
            <p className="mb-4 text-sm text-red-600" role="alert">
              {staffErrorMessage}
            </p>
          ) : null}

          {staffLoading ? (
            <FetchLoadingState message="Loading department staff..." className="min-h-[220px]" />
          ) : departmentStaff.length === 0 ? (
            <p className="text-sm text-gray-600 py-4">No staff found in this department yet.</p>
          ) : (
            <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
              {departmentStaff.map((staff) => {
                const isCurrentHead = !!staff?.isHeadOfDepartment;
                const contact = staff?.officialEmail || staff?.personalEmail || staff?.phone || "No contact info";
                const staffEmployeeId = getDepartmentStaffEmployeeId(staff);

                return (
                  <div
                    key={staffEmployeeId || staff.id}
                    className="rounded-xl border border-gray-200 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
                  >
                    <div className="flex items-center gap-3">
                      <img
                        src={resolveEmployeeImage(staff?.profilePhotoUrl, formatEmployeeName(staff))}
                        alt={formatEmployeeName(staff)}
                        className="w-14 h-14 rounded-full object-cover border border-gray-200"
                      />

                      <div>
                        <p className="font-semibold text-[#071C50]">{formatEmployeeName(staff)}</p>
                        <p className="text-sm text-gray-600">{staff?.positionName || "Staff"}</p>
                        <p className="text-xs text-gray-500">{contact}</p>
                        <p className="text-xs text-gray-500">Employee ID: {staff?.employeeNumber || staff?.id}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/employee/employeespage?id=${encodeURIComponent(staffEmployeeId || staff.id)}`)}
                        className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm"
                      >
                        View Profile
                      </button>

                      <button
                        type="button"
                        onClick={() => handleAssignHeadOfDepartment(staff)}
                        disabled={isCurrentHead || assigningHeadId === staffEmployeeId}
                        className="px-3 py-2 rounded-lg text-sm bg-[#1234CD] text-white disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {isCurrentHead
                          ? "Current HOD"
                          : assigningHeadId === staffEmployeeId
                            ? "Assigning..."
                            : "Make HOD"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </InviteModal>

      <InviteModal isOpen={openManageStaffModal} onClose={() => !membersSaving && setOpenManageStaffModal(false)}>
        <div className="my-4">
          <h2 className="text-xl font-semibold text-[#071C50]">Manage Department Employees</h2>
          <p className="mt-1 text-sm text-slate-500">Select all employees who should belong to {title}. You can add or remove several employees in one update.</p>
          <input
            type="search"
            value={employeeSearch}
            onChange={(event) => setEmployeeSearch(event.target.value)}
            placeholder="Search by name, employee number or email"
            className="mt-5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1234CD]"
          />
          {membersLoading ? <FetchLoadingState message="Loading employees..." className="min-h-[300px]" /> : (
            <div className="mt-4 max-h-[420px] space-y-2 overflow-y-auto pr-1">
              {availableEmployees.filter((employee) => {
                const query = employeeSearch.trim().toLowerCase();
                if (!query) return true;
                return [employee.firstName, employee.lastName, employee.employeeNumber, employee.officialEmail, employee.personalEmail]
                  .some((value) => `${value || ""}`.toLowerCase().includes(query));
              }).map((employee) => {
                const checked = selectedMemberIds.includes(employee.id);
                const wasAlreadySelected = initialMemberIds.includes(employee.id);
                const hasPrimaryDepartment = Boolean(employee.departmentId || employee.departmentName);
                const name = formatEmployeeName(employee);
                return <label key={employee.id} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 ${checked ? "border-blue-200 bg-blue-50" : "border-slate-100 bg-white"}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={membersSaving}
                    onChange={() => setSelectedMemberIds((current) => checked ? current.filter((id) => id !== employee.id) : [...current, employee.id])}
                    className="h-4 w-4 accent-[#1234CD]"
                  />
                  <img src={resolveEmployeeImage(employee.profilePhotoUrl, name)} alt="" className="h-10 w-10 rounded-full object-cover" />
                  <span className="min-w-0 flex-1"><span className="block truncate font-medium text-[#071C50]">{name}</span><span className="block truncate text-xs text-slate-500">{employee.employeeNumber || employee.officialEmail || employee.personalEmail || "Employee"}{employee.departmentName ? ` · Primary: ${employee.departmentName}` : ""}</span></span>
                  <span className="text-right text-xs font-semibold text-slate-400">{!checked ? "Not selected" : wasAlreadySelected ? "Current member" : hasPrimaryDepartment ? "Add as secondary" : "Add as primary"}</span>
                </label>;
              })}
            </div>
          )}
          {!membersLoading ? <div className="mt-5 rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
            {selectedMemberIds.filter((id) => !initialMemberIds.includes(id)).length} to add · {initialMemberIds.filter((id) => !selectedMemberIds.includes(id)).length} to remove · {selectedMemberIds.length} selected
          </div> : null}
          <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button type="button" disabled={membersSaving} onClick={() => setOpenManageStaffModal(false)} className="rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 disabled:opacity-50">Cancel</button>
            <button type="button" disabled={membersLoading || membersSaving} onClick={saveDepartmentEmployees} className="rounded-xl bg-[#1234CD] px-5 py-2.5 text-sm font-semibold text-white disabled:cursor-wait disabled:opacity-50">{membersSaving ? "Saving changes..." : "Save employee changes"}</button>
          </div>
        </div>
      </InviteModal>

      <DeleteItem
        isOpen={openDeleteModal}
        onClose={closeDeleteModal}
      >
        <div>
          <img
            className="w-40 mx-auto animate-pulse"
            src={DeleteImg}
            alt="delete department"
          />

          <h1 className="text-lg text-center text-[#000000] font-bold">
            Continue to delete this department?
          </h1>
          <p className="text-sm text-center mt-2">Action cannot be undone.</p>
          {deleteErrorMessage && (
            <p className="text-sm text-center mt-3 text-red-600" role="alert">
              {deleteErrorMessage}
            </p>
          )}
        </div>
        <div className="flex items-center py-4 gap-4 my-4">
          <button
            onClick={closeDeleteModal}
            className="border-[#1233CC] py-1 border-2 text-[#1233CC] font-medium px-4 rounded-[10px] w-[102px]"
          >
            No
          </button>
          <button
            className="text-[16px] font-medium px-4 py-1 bg-[#1233CC] rounded-[10px] w-[102px] text-white disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={handleDeleteDepartment}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Yes"}
          </button>
        </div>
      </DeleteItem>

      <SuccessfulDelete
        isOpen={openDeleteSuccessModal}
        onClose={() => setOpenDeleteSuccessModal(false)}
      >
        <div>
          <img
            src={verifyImg}
            alt="department deleted"
            className="w-20 animate-ping mx-auto"
          />
          <div className="text-center mt-6">
            <h2 className="text-black text-[20px] leading-[27px] font-bold mb-2">
              Department deleted
            </h2>
            <p className="text-[#74777B] text-[14px] leading-[18.9px]">
              The department was removed successfully.
            </p>
          </div>
        </div>
      </SuccessfulDelete>

      <InviteModal
        isOpen={openEditModal}
        onClose={() => setOpenEditModal(false)}
      >
        <div className="my-8">
          <div>
            <p className="text-2xl font-semibold text-black font-Outfit">
              Edit Department
            </p>
          </div>
          <form
            className="my-12"
            onSubmit={handleUpdateDepartment}
          >
            {formErrorMessage && (
              <p className="mb-4 text-sm text-red-600" role="alert">
                {formErrorMessage}
              </p>
            )}

            <div>
              <label
                htmlFor="department-name"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Department Name
              </label>
              <input
                type="text"
                id="department-name"
                value={departmentName}
                onChange={(ev) => setDepartmentName(ev.target.value)}
                className="w-full p-2 text-center mb-2 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div>
              <label
                htmlFor="department-description"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Description
              </label>
              <textarea
                id="department-description"
                value={description}
                onChange={(ev) => setDescription(ev.target.value)}
                rows={4}
                wrap="soft"
                className="w-full p-2 min-h-36 mb-4 resize-y whitespace-pre-wrap break-words rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div className="flex justify-center gap-12 mt-20 items-end">
              <SimpleButton
                onClick={() => setOpenEditModal(false)}
                type="button"
                className="font-[700] font-satoshi leading-[21.6px] flex justify-center items-center border w-40 rounded-xl py-[16px] border-[#1234CD] text-[#1234CD] text-[16px]"
              >
                Cancel
              </SimpleButton>

              <SimpleButton
                type="submit"
                className="font-[700] font-satoshi leading-[21.6px] flex justify-center items-center w-40 rounded-xl text-white py-[16px] bg-[#1234CD] text-[16px] disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={isSaving}
              >
                <span>{isSaving ? "Updating..." : "Update"}</span>
              </SimpleButton>
            </div>
          </form>
        </div>
      </InviteModal>

      <SuccessModal
        isOpen={openSuccessModal}
        onClose={() => setOpenSuccessModal(false)}
      />
    </div>
  );
};

export default DepartmentViewdetails;
